import { MAP_SIZE } from '../game/worldConstants';
import {
  clampCanvasOffset,
  DEFAULT_LEVEL_CANVAS_HEIGHT,
  DEFAULT_LEVEL_CANVAS_WIDTH,
  normalizeCanvasDimensions,
} from '../lib/levelEditorCanvas';
import { DEFAULT_PENCIL_COLOR } from '../lib/levelEditorColors';
import {
  DEFAULT_OCCLUSION_OVERLAY_OPACITY,
  type OcclusionMaskPaintState,
} from '../lib/levelEditorOcclusionMask';
import {
  DEFAULT_TRAVERSAL_BRUSH_SIZE,
  DEFAULT_TRAVERSAL_PAINT_STATE,
  getTraversalBrushBounds,
} from '../lib/levelEditorTraversalBrush';
import { clampAnnotationsToLayout } from '../lib/worldAnnotations';
import type {
  EditorLayer,
  LayerCell,
  LevelDesignDocument,
  PencilTarget,
  TraversalState,
  UploadedImageTransformSnapshot,
} from '../types/levelEditor';
import type { WorldAnnotation } from '../types/worldAnnotation';

import { cloneAnnotations } from './editorStoreCloning';
import type { TraversalChange } from './editorStoreCommands';
import {
  cloneLayerCell,
  cloneLayers,
  isAnimatedGifLayer,
  makeTileKey,
  parseTileKey,
} from './editorStoreLayerCells';

export {
  cloneAnnotation,
  cloneAnnotations,
  cloneBrush,
  cloneCompositeStamp,
  cloneTileReference,
} from './editorStoreCloning';
export {
  applyEditorCommandWithDirection,
  applyTileChanges,
  applyTraversalChanges,
  omitRecordKey,
  pushUndo,
} from './editorStoreCommands';
export type {
  AnnotationChange,
  EditorCommand,
  SpawnChange,
  TileChange,
  TraversalChange,
} from './editorStoreCommands';
export {
  cloneLayerCell,
  cloneLayers,
  createColorCell,
  isAnimatedGifLayer,
  isColorCell,
  isSameTile,
  makeTileKey,
  normalizeLayerLookupName,
  normalizeLayerName,
  parseTileKey,
  tryCreateBrushFromTile,
} from './editorStoreLayerCells';

export const DEFAULT_TILESET_PACK = 'moderninteriors-16x16';
export const DEFAULT_TILESET_COLLECTION_ID = 'moderninteriors';
export const DEFAULT_TRAVERSAL_OVERLAY_OPACITY = 0.55;
export const DEFAULT_BASE_TRAVERSAL_STATE: TraversalState = 'blocked';
export const DEFAULT_OCCLUSION_PAINT_STATE: OcclusionMaskPaintState = 'occlude';

export const clampOpacity = (opacity: number): number => Math.min(1, Math.max(0.05, opacity));

export const createDefaultLayers = (): EditorLayer[] => [
  {
    id: 'layer-floor',
    name: 'Floor',
    layerType: 'tile',
    visible: true,
    locked: false,
    opacity: 1,
    traversalDefault: 'walkable',
    tiles: {},
  },
  {
    id: 'layer-walls',
    name: 'Walls',
    layerType: 'tile',
    visible: true,
    locked: false,
    opacity: 1,
    traversalDefault: 'blocked',
    tiles: {},
  },
  {
    id: 'layer-furniture',
    name: 'Furniture',
    layerType: 'tile',
    visible: true,
    locked: false,
    opacity: 1,
    traversalDefault: 'blocked',
    tiles: {},
  },
  {
    id: 'layer-decoration',
    name: 'Decoration',
    layerType: 'tile',
    visible: true,
    locked: false,
    opacity: 1,
    traversalDefault: 'walkable',
    tiles: {},
  },
];

const createEphemeralEditorState = () => ({
  activeCompositeStamp: null,
  activeTool: 'draw' as const,
  isDirty: false,
  panX: 0,
  panY: 0,
  pencilBrushSize: DEFAULT_TRAVERSAL_BRUSH_SIZE,
  pencilColor: DEFAULT_PENCIL_COLOR,
  pencilTarget: 'layer' as PencilTarget,
  pencilTraversalState: DEFAULT_TRAVERSAL_PAINT_STATE,
  pencilOcclusionState: DEFAULT_OCCLUSION_PAINT_STATE,
  redoStack: [],
  selectedBrush: null,
  showOcclusionOverlay: false,
  occlusionOverlayOpacity: DEFAULT_OCCLUSION_OVERLAY_OPACITY,
  showTraversalOverlay: false,
  traversalOverlayOpacity: DEFAULT_TRAVERSAL_OVERLAY_OPACITY,
  undoStack: [],
  zoom: 1,
});

export interface ResetDesignStateConfig {
  activeTilesetCollectionId?: string;
  canvasHeight?: number;
  canvasWidth?: number;
  designName?: string;
  tilesetPack?: string;
}

export const createResetDesignState = (state: ResetDesignStateConfig) => {
  const { canvasWidth, canvasHeight } = normalizeCanvasDimensions(
    state.canvasWidth,
    state.canvasHeight,
    DEFAULT_LEVEL_CANVAS_WIDTH,
    DEFAULT_LEVEL_CANVAS_HEIGHT,
  );
  return {
    ...createEphemeralEditorState(),
    activeDesignId: null,
    activeLayerId: 'layer-floor',
    activeTilesetCollectionId: state.activeTilesetCollectionId || DEFAULT_TILESET_COLLECTION_ID,
    canvasHeight,
    canvasWidth,
    designName: state.designName?.trim().slice(0, 40) || 'Untitled Design',
    layers: createDefaultLayers(),
    mapSize: Math.max(canvasWidth, canvasHeight),
    occlusionMaskUrl: null,
    spawnPoint: { x: Math.floor(canvasWidth / 2), y: Math.floor(canvasHeight / 2) },
    tilesetPack: state.tilesetPack || DEFAULT_TILESET_PACK,
    traversalOverrides: {},
    annotations: [],
    activeAnnotationId: null,
  };
};

export const createLoadedDesignState = (design: LevelDesignDocument) => {
  const layers = design.layers.length > 0 ? cloneLayers(design.layers) : createDefaultLayers();
  const { canvasWidth, canvasHeight } = normalizeCanvasDimensions(
    design.canvasWidth,
    design.canvasHeight,
    MAP_SIZE,
    MAP_SIZE,
  );
  return {
    ...createEphemeralEditorState(),
    activeDesignId: design.id,
    activeLayerId: layers[0].id,
    canvasHeight,
    canvasWidth,
    designName: design.name,
    layers,
    mapSize: Math.max(canvasWidth, canvasHeight),
    occlusionMaskUrl: design.occlusionMaskUrl ?? null,
    spawnPoint: {
      x: Math.max(0, Math.min(canvasWidth - 1, Math.floor(design.spawnPoint.x))),
      y: Math.max(0, Math.min(canvasHeight - 1, Math.floor(design.spawnPoint.y))),
    },
    tilesetPack: design.tilesetPack,
    traversalOverrides: { ...design.traversalOverrides },
    annotations: cloneAnnotations(design.annotations),
    activeAnnotationId: null,
  };
};

const cropLayerTilesToCanvas = (
  tiles: Record<string, LayerCell>,
  canvasWidth: number,
  canvasHeight: number,
): Record<string, LayerCell> => {
  const nextTiles: Record<string, LayerCell> = {};
  for (const [key, tile] of Object.entries(tiles)) {
    const { x, y } = parseTileKey(key);
    if (!ensureBounds(x, y, canvasWidth, canvasHeight)) {
      continue;
    }
    nextTiles[key] = cloneLayerCell(tile);
  }
  return nextTiles;
};

const cropTraversalOverridesToCanvas = (
  overrides: Record<string, TraversalState>,
  canvasWidth: number,
  canvasHeight: number,
): Record<string, TraversalState> => {
  const nextOverrides: Record<string, TraversalState> = {};
  for (const [key, value] of Object.entries(overrides)) {
    const { x, y } = parseTileKey(key);
    if (!ensureBounds(x, y, canvasWidth, canvasHeight)) {
      continue;
    }
    nextOverrides[key] = value;
  }
  return nextOverrides;
};

const clampSpawnPointToCanvas = (
  spawnPoint: { x: number; y: number },
  canvasWidth: number,
  canvasHeight: number,
): { x: number; y: number } => ({
  x: clampCanvasOffset(spawnPoint.x, canvasWidth),
  y: clampCanvasOffset(spawnPoint.y, canvasHeight),
});

const clampUploadedImageTransformToCanvas = (
  transform: UploadedImageTransformSnapshot | undefined,
  canvasWidth: number,
  canvasHeight: number,
): UploadedImageTransformSnapshot | undefined => {
  if (!transform) {
    return undefined;
  }
  return {
    ...transform,
    offsetX: clampCanvasOffset(transform.offsetX, canvasWidth),
    offsetY: clampCanvasOffset(transform.offsetY, canvasHeight),
  };
};

export const resizeEditorDesignState = (input: {
  annotations: WorldAnnotation[];
  canvasHeight: number;
  canvasWidth: number;
  layers: EditorLayer[];
  occlusionMaskUrl: string | null;
  spawnPoint: { x: number; y: number };
  traversalOverrides: Record<string, TraversalState>;
}) => {
  const { canvasWidth, canvasHeight } = normalizeCanvasDimensions(
    input.canvasWidth,
    input.canvasHeight,
    DEFAULT_LEVEL_CANVAS_WIDTH,
    DEFAULT_LEVEL_CANVAS_HEIGHT,
  );

  return {
    canvasHeight,
    canvasWidth,
    layers: input.layers.map((layer) => {
      const uploadedImageTransform = clampUploadedImageTransformToCanvas(
        layer.uploadedImageTransform,
        canvasWidth,
        canvasHeight,
      );
      return {
        ...layer,
        tiles: cropLayerTilesToCanvas(layer.tiles, canvasWidth, canvasHeight),
        ...(uploadedImageTransform ? { uploadedImageTransform } : {}),
      };
    }),
    mapSize: Math.max(canvasWidth, canvasHeight),
    occlusionMaskUrl: input.occlusionMaskUrl,
    spawnPoint: clampSpawnPointToCanvas(input.spawnPoint, canvasWidth, canvasHeight),
    traversalOverrides: cropTraversalOverridesToCanvas(
      input.traversalOverrides,
      canvasWidth,
      canvasHeight,
    ),
    annotations: clampAnnotationsToLayout(input.annotations, {
      tileWidth: canvasWidth,
      tileHeight: canvasHeight,
    }),
  };
};

export const ensureBounds = (
  x: number,
  y: number,
  canvasWidth: number,
  canvasHeight = canvasWidth,
): boolean => x >= 0 && x < canvasWidth && y >= 0 && y < canvasHeight;

export const resolveTraversalFromLayers = (
  layers: EditorLayer[],
  x: number,
  y: number,
): TraversalState => {
  const key = makeTileKey(x, y);
  for (const layer of layers) {
    if (!isAnimatedGifLayer(layer) && layer.traversalDefault === 'blocked' && layer.tiles[key]) {
      return 'blocked';
    }
  }
  return 'walkable';
};

export const getBaseTraversalAtFromState = (
  overrides: Record<string, TraversalState>,
  x: number,
  y: number,
): TraversalState => overrides[makeTileKey(x, y)] ?? DEFAULT_BASE_TRAVERSAL_STATE;

export const getTraversalAtFromState = (
  layers: EditorLayer[],
  overrides: Record<string, TraversalState>,
  x: number,
  y: number,
): TraversalState => {
  if (resolveTraversalFromLayers(layers, x, y) === 'blocked') {
    return 'blocked';
  }
  return getBaseTraversalAtFromState(overrides, x, y);
};

export const buildTraversalPaintChanges = (input: {
  brushSize: number;
  canvasWidth?: number;
  canvasHeight?: number;
  layers: EditorLayer[];
  mapSize?: number;
  overrides: Record<string, TraversalState>;
  paintState: TraversalState;
  x: number;
  y: number;
}): TraversalChange[] => {
  const { endX, endY, startX, startY } = getTraversalBrushBounds({
    mapSize: input.mapSize,
    canvasWidth: input.canvasWidth,
    canvasHeight: input.canvasHeight,
    size: input.brushSize,
    x: input.x,
    y: input.y,
  });
  const changes: TraversalChange[] = [];

  for (let tileY = startY; tileY <= endY; tileY += 1) {
    for (let tileX = startX; tileX <= endX; tileX += 1) {
      const key = makeTileKey(tileX, tileY);
      const before = input.overrides[key] ?? null;
      const after = input.paintState === DEFAULT_BASE_TRAVERSAL_STATE ? null : input.paintState;
      if (before === after) {
        continue;
      }
      changes.push({ key, before, after });
    }
  }

  return changes;
};

export const getOccupiedTileBounds = (
  layers: EditorLayer[],
): { minX: number; minY: number; maxX: number; maxY: number } | null => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const layer of layers) {
    for (const key of Object.keys(layer.tiles)) {
      const { x, y } = parseTileKey(key);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (!Number.isFinite(minX)) {
    return null;
  }
  return { minX, minY, maxX, maxY };
};
